import { useCallback, useEffect, useRef } from 'react'
import {
  useAuth,
  useFirestore,
  useFirestoreCollectionData,
  useFunctions,
  useSigninCheck,
  useUser,
} from 'reactfire'
import { collection, query, where } from 'firebase/firestore'
import { signInWithCustomToken } from 'firebase/auth'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { logger } from '../lib/logger'
import { playApprovalAlert } from '../lib/audioAlert'
import { claimApprovalAlert } from '../lib/claimApprovalAlert'
import { useTabKeepAlive } from '../lib/useTabKeepAlive'
import { useIsAutomationAdmin } from '../lib/useIsAutomationAdmin'
import { useAgentSoundEnabled } from '../lib/useAgentSoundEnabled'
import { api } from '../lib/api'
import { markdownToPlain } from '../lib/markdownToPlain'

interface NotifiableRun {
  uid: string
  status?: 'waiting_for_approval' | 'needs_operator_review'
  approvalMessage?: string
  errorMessage?: string | null
}

// TODO: remove automation admin gate when Pulse is GA
export function AgentRunNotificationListener() {
  const { isAutomationAdmin, loading } = useIsAutomationAdmin()

  if (loading || !isAutomationAdmin) return null

  return <AgentRunNotificationListenerInner />
}

function AgentRunNotificationListenerInner() {
  const auth = useAuth()
  const firestore = useFirestore()
  const functions = useFunctions()
  const user = useUser()
  const navigate = useNavigate()
  const { t } = useTranslation()
  const seenRunIds = useRef<Set<string>>(new Set())
  const initialLoad = useRef(true)
  const pendingRuns = useRef<NotifiableRun[]>([])
  const soundEnabled = useAgentSoundEnabled()

  const { data: impersonationCheck } = useSigninCheck({
    requiredClaims: { impersonating: true },
  })
  const isImpersonating = impersonationCheck?.hasRequiredClaims === true

  useTabKeepAlive()

  const currentUid = user.data?.uid ?? null

  const showNotification = useCallback(
    (run: NotifiableRun) => {
      if (soundEnabled) {
        const isViewingRun =
          window.location.pathname === `/admin/automation/${run.uid}`
        if (!isViewingRun) {
          // Claimed per run so an operator with several windows open hears this
          // once. The window showing the run is skipped here because the run
          // detail page rings for it, and it claims through the same lock.
          void claimApprovalAlert(run.uid).then((won) => {
            if (won) playApprovalAlert()
          })
        }
      }
      try {
        const isReviewRun = run.status === 'needs_operator_review'
        const notification = new Notification(
          isReviewRun
            ? t('frontend:pulse.notification.operatorReviewRequired')
            : t('frontend:pulse.status.approvalRequired'),
          {
            body: isReviewRun
              ? markdownToPlain(run.errorMessage) ||
                t('frontend:pulse.notification.escalated')
              : markdownToPlain(run.approvalMessage) ||
                t('frontend:pulse.notification.waitingApproval'),
            tag: `agent-run-${run.uid}`,
            // Sticky on Chrome/Edge/Firefox desktop; ignored on Safari and mobile.
            // Approval requests block the run, so the notification must persist
            // until the operator dismisses or clicks it.
            requireInteraction: true,
          }
        )
        notification.onclick = () => {
          window.focus()
          notification.close()
          const targetPath =
            run.status === 'needs_operator_review'
              ? `/admin/automation/${run.uid}`
              : `/admin/automation/${run.uid}#approval`
          if (isImpersonating) {
            api(functions, 'impersonate/stop')
              .then(async (result: unknown) => {
                const data = result as { customToken?: string } | undefined
                if (data?.customToken) {
                  await signInWithCustomToken(auth, data.customToken)
                }
                window.location.replace(targetPath)
              })
              .catch(() => {
                window.location.replace(targetPath)
              })
          } else {
            navigate(targetPath)
          }
        }
      } catch (error) {
        logger.warn('Failed to create browser notification', {
          error: String(error),
        })
      }
    },
    [navigate, soundEnabled, isImpersonating, auth, functions, t]
  )

  useEffect(() => {
    if (!('Notification' in window) || Notification.permission !== 'default')
      return
    let cancelled = false
    Notification.requestPermission()
      .then((result) => {
        if (cancelled) return
        if (result === 'granted') {
          for (const run of pendingRuns.current) {
            seenRunIds.current.add(run.uid)
            showNotification(run)
          }
        } else {
          for (const run of pendingRuns.current) {
            seenRunIds.current.add(run.uid)
          }
        }
        pendingRuns.current = []
      })
      .catch((error) => {
        logger.warn('Notification permission request failed', {
          error: String(error),
        })
      })
    return () => {
      cancelled = true
    }
  }, [showNotification])

  const { data: notifiableRuns } = useFirestoreCollectionData(
    query(
      collection(firestore, 'agent-runs'),
      where('status', 'in', ['waiting_for_approval', 'needs_operator_review'])
    ),
    { idField: 'uid' }
  )

  useEffect(() => {
    if (!notifiableRuns || !currentUid) return

    if (initialLoad.current) {
      for (const run of notifiableRuns) {
        seenRunIds.current.add(run.uid)
      }
      initialLoad.current = false
      return
    }

    const pendingRunIds = new Set(pendingRuns.current.map((r) => r.uid))
    // Every automation_admin gets notified for every approval. The outer
    // <AgentRunNotificationListener /> already gates on the claim, so reaching
    // this code means the user is an admin. We intentionally ignore the
    // per-run notificationRecipients allowlist here so that any admin browsing
    // the app, on any page, hears the alert and sees the OS-level
    // notification when a run blocks on approval.
    const newRuns = (notifiableRuns as NotifiableRun[]).filter(
      (run) => !seenRunIds.current.has(run.uid) && !pendingRunIds.has(run.uid)
    )

    for (const run of newRuns) {
      if (Notification.permission === 'granted') {
        seenRunIds.current.add(run.uid)
        showNotification(run)
      } else if (Notification.permission === 'default') {
        pendingRuns.current.push(run)
      } else {
        seenRunIds.current.add(run.uid)
      }
    }
  }, [notifiableRuns, currentUid, showNotification])

  return null
}
